/* eslint-disable no-param-reassign */
import { nextTick, unref } from 'vue';
import type { Ref } from 'vue';

export function useDropdownScroll(dropdownRef: Ref, className: string) {
    let lastScrollTop = 0;

    function getDropdownWrap(): HTMLElement | null {
        return dropdownRef.value?.$el?.querySelector(`.${className}`) ?? null;
    }

    function scrollToOption(option: any) {
        if (!option?.optionRef) return;

        nextTick(() => {
            requestAnimationFrame(() => {
                const dropdownWrap = getDropdownWrap();
                if (!dropdownWrap?.offsetHeight) return;
                let target = option.optionRef;
                if (!dropdownWrap || !target) return;

                if (target?.$el) {
                    target = target.$el;
                } else {
                    target = unref(target);
                }

                const dropdownRect = dropdownWrap.getBoundingClientRect();
                const optionRect = target?.getBoundingClientRect();

                const isAbove = optionRect.top < dropdownRect.top;
                const isBelow = optionRect.bottom > dropdownRect.bottom;

                if (isAbove || isBelow) {
                    dropdownWrap.scrollTop += target.offsetTop - dropdownWrap.scrollTop;
                }
            });
        });
    }

    function setScrollPosition(scrollTop?: number) {
        if (typeof scrollTop !== 'number') return;

        const dropdownWrap = getDropdownWrap();
        if (dropdownWrap) {
            nextTick(() => {
                dropdownWrap.scrollTop = scrollTop;
            });
        }
    }

    function receiveScrollTop(): number {
        const dropdownWrap = getDropdownWrap();
        if (dropdownWrap) {
            lastScrollTop = dropdownWrap.scrollTop;
            return lastScrollTop;
        }
        return 0;
    }

    function scrollToHoverOption(hoverOption: any) {
        scrollToOption(hoverOption);
    }

    function updateHoverOption(index: number, options: any[], hoverOptionRef: Ref) {
        const option = options[index] || null;
        hoverOptionRef.value = option;

        scrollToOption(option);

        // eslint-disable-next-line no-restricted-syntax
        for (const o of options) {
            o.hover = o === option;
        }
    }

    function setDefaultHoverIndex(
        hoverIndexRef: Ref<number>,
        options: any[],
        query: string,
    ) {
        hoverIndexRef.value = -1;

        for (let i = 0; i < options.length; ++i) {
            const option = options[i];
            const isAvailable = !option.disabled && !option.groupDisabled && option.visible;
            const shouldSelect = query.length > 0 ? isAvailable : option.isSelected;
            if (shouldSelect) {
                hoverIndexRef.value = i;
                break;
            }
        }
    }

    return {
        scrollToOption,
        scrollToHoverOption,
        updateHoverOption,
        setScrollPosition,
        setDefaultHoverIndex,
        receiveScrollTop,
        getLastScrollTop: () => lastScrollTop,
    };
}
